import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import EditNoteIcon from '@mui/icons-material/EditNote';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import OButton from '../../../UI/FormControls/OButton';
import { colors } from '../../../../../shared/colors';
import { icons } from '../../../../../shared/icons';
import useIzvestajRadnogNaloga, {
  JEZIK_IZVESTAJA,
  TIP_IZVESTAJA,
} from './useIzvestajRadnogNaloga';
import { podrazumevaniMejl, pripremiResurse } from './generisiIzvestajPDF';
import {
  danasnjiDan,
  podrazumevaniMejlWorkReporta,
} from './generisiWorkReportPDF';
import IzmenaIzvestajaDialog from './IzmenaIzvestajaDialog';
import IzmenaWorkReportaDialog from './IzmenaWorkReportaDialog';

const AKCIJA = { PREUZMI: 'preuzmi', MEJL: 'mejl' };
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SVETLO_NARANDZASTA = '#fff4ea';
const IKONICE_IZVORA = {
  kupac: <BusinessIcon fontSize="small" />,
  kontakt: <PersonOutlineIcon fontSize="small" />,
  rucno: <AlternateEmailIcon fontSize="small" />,
};

const razdvojiAdrese = (tekst) =>
  (tekst || '').split(/[;,\s]+/).filter(Boolean);
const istaAdresa = (a, b) => a.toLowerCase() === b.toLowerCase();

const NaslovSekcije = ({ children }) => (
  <Typography
    sx={{
      fontSize: 12,
      fontWeight: 600,
      color: '#666',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

const OpcijaAkcije = ({ izabrana, ikonica, naslov, opis, onClick }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
    sx={{
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: 1.5,
      borderRadius: 2,
      cursor: 'pointer',
      border: `2px solid ${izabrana ? colors.ORANGE : '#e0e0e0'}`,
      backgroundColor: izabrana ? SVETLO_NARANDZASTA : colors.WHITE,
      transition: 'all .15s',
      '&:hover': { borderColor: colors.ORANGE },
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: izabrana ? colors.ORANGE : '#f2f2f2',
        color: izabrana ? colors.WHITE : '#666',
      }}
    >
      {ikonica}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{naslov}</Typography>
      <Typography
        sx={{ fontSize: 12, color: 'text.secondary', wordBreak: 'break-word' }}
      >
        {opis}
      </Typography>
    </Box>
  </Box>
);

const IzvestajRadnogNalogaDialog = ({ open, idDok, tip, onClose }) => {
  const { t, i18n } = useTranslation();
  const {
    preuzmiIzvestaj,
    posaljiIzvestaj,
    ucitajPodatke,
    ucitajZahtev,
    napraviModel,
    ucitajIzvestaj,
    pregledIzvestaja,
    isLoading,
  } = useIzvestajRadnogNaloga();
  const [pregledUrl, setPregledUrl] = useState(null);
  const podaciRef = useRef(null);
  const zahteviRef = useRef({});
  const pocetniJezik = () =>
    i18n.language === JEZIK_IZVESTAJA.ENG
      ? JEZIK_IZVESTAJA.ENG
      : JEZIK_IZVESTAJA.SRB;

  const [jezik, setJezik] = useState(pocetniJezik);
  const [akcija, setAkcija] = useState(AKCIJA.PREUZMI);
  const [primaoci, setPrimaoci] = useState([]);
  const [predlozi, setPredlozi] = useState([]);
  const [info, setInfo] = useState({});
  const [emailoviKupca, setEmailoviKupca] = useState([]);
  const [ucitavanje, setUcitavanje] = useState(false);
  const [unos, setUnos] = useState('');
  const [greska, setGreska] = useState('');
  const [izvestaj, setIzvestaj] = useState(null);
  const [urednik, setUrednik] = useState(null);
  const [mejl, setMejl] = useState(null);
  const [datum, setDatum] = useState(danasnjiDan);

  const imaPristup = localStorage.uniqueUser === 'true';
  const jeWorkReport = tip === TIP_IZVESTAJA.RN;

  useEffect(() => {
    if (!open || !idDok || !imaPristup) return undefined;
    setJezik(pocetniJezik());
    setDatum(danasnjiDan());
    setAkcija(AKCIJA.PREUZMI);
    setIzvestaj(null);
    setUrednik(null);
    setMejl(null);
    setPrimaoci([]);
    setPredlozi([]);
    setInfo({});
    setEmailoviKupca([]);
    setUnos('');
    setGreska('');

    let aktivan = true;
    setUcitavanje(true);
    pripremiResurse();
    const pocetni = pocetniJezik();
    const ucitano = ucitajPodatke(idDok, tip, pocetni);
    podaciRef.current = ucitano.then((r) => r.podaci).catch(() => null);
    zahteviRef.current = {
      [pocetni]: ucitano.then((r) => r.podaci.zahtev).catch(() => null),
    };
    ucitano
      .then(({ primaoci: { ceoBrDok, nazivKupca, kupac, kontakti } }) => {
        if (!aktivan) return;
        const adreseKupca = kupac
          ? razdvojiAdrese(kupac.email)
              .filter((email) => EMAIL_REGEX.test(email))
              .map((email) => ({
                email,
                naziv: t('pdfDialog.kupac'),
                izvor: 'kupac',
              }))
          : [];
        const adreseKontakata = kontakti
          .map((kontakt) => ({
            email: kontakt.email.trim(),
            naziv: `${kontakt.ime || ''} ${kontakt.prezime || ''}`.trim(),
            izvor: 'kontakt',
          }))
          .filter((kontakt) => EMAIL_REGEX.test(kontakt.email));
        setInfo({ ceoBrDok, nazivKupca });
        setEmailoviKupca(adreseKupca.map((adresa) => adresa.email));
        setPrimaoci(adreseKupca);
        setPredlozi([...adreseKupca, ...adreseKontakata]);
      })
      .catch(console.error)
      .finally(() => aktivan && setUcitavanje(false));
    return () => {
      aktivan = false;
    };
  }, [open, idDok, tip]);

  const zahtevZaJezik = async (podaci) => {
    const idZahteva = podaci?.zaglavlje.idDokServisnogZahteva;
    if (tip === TIP_IZVESTAJA.PUR || !idZahteva) return null;
    if (!zahteviRef.current[jezik]) {
      zahteviRef.current[jezik] = ucitajZahtev(idZahteva, jezik).catch(
        () => null,
      );
    }
    return zahteviRef.current[jezik];
  };

  useEffect(() => {
    podaciRef.current?.then(zahtevZaJezik);
  }, [jezik]);

  // Podaci učitani pri otvaranju popupa; null ako učitavanje nije uspelo
  const originalniIzvestaj = async () => {
    const podaci = await podaciRef.current;
    if (!podaci) return null;
    return napraviModel(
      tip,
      {
        ...podaci,
        zahtev: await zahtevZaJezik(podaci),
      },
      datum,
    );
  };

  const izmeniDatum = (vrednost) => {
    if (!vrednost) return;
    setDatum(vrednost);
    setIzvestaj(null);
  };

  const dodajPrimaoca = (primalac) =>
    setPrimaoci((prethodni) =>
      prethodni.some((p) => istaAdresa(p.email, primalac.email))
        ? prethodni
        : [...prethodni, primalac],
    );
  const ukloniPrimaoca = (email) =>
    setPrimaoci((prethodni) =>
      prethodni.filter((p) => !istaAdresa(p.email, email)),
    );

  const potvrdiUnos = () => {
    const adrese = razdvojiAdrese(unos);
    if (!adrese.length) return;
    const neispravne = adrese.filter((email) => !EMAIL_REGEX.test(email));
    adrese
      .filter((email) => EMAIL_REGEX.test(email))
      .forEach((email) => dodajPrimaoca({ email, izvor: 'rucno' }));
    setUnos(neispravne.join(', '));
    setGreska(neispravne.length ? t('pdfDialog.neispravnaAdresa') : '');
  };

  const handleIzmeni = async () => {
    const model =
      izvestaj ||
      (await ucitajIzvestaj(idDok, tip, jezik, originalniIzvestaj()));
    if (model) setUrednik(model);
  };

  const handlePregled = async () => {
    const model =
      izvestaj ||
      (await ucitajIzvestaj(idDok, tip, jezik, originalniIzvestaj()));
    if (!model) return;
    const url = await pregledIzvestaja(model, jezik);
    if (url) setPregledUrl(url);
  };
  const zatvoriPregled = () => {
    if (pregledUrl) URL.revokeObjectURL(pregledUrl);
    setPregledUrl(null);
  };

  const ceoBrDok =
    izvestaj?.zaglavlje?.ceoBrDok || izvestaj?.broj || info.ceoBrDok;
  const mejlZaTip = () =>
    jeWorkReport
      ? podrazumevaniMejlWorkReporta(jezik, ceoBrDok, datum)
      : podrazumevaniMejl(tip, jezik, ceoBrDok);
  const tekstMejla = mejl || mejlZaTip();
  const izmeniMejl = (polje, vrednost) =>
    setMejl({ ...tekstMejla, [polje]: vrednost });

  const handlePotvrdi = async () => {
    if (akcija === AKCIJA.PREUZMI) {
      if (
        await preuzmiIzvestaj(
          idDok,
          tip,
          jezik,
          izvestaj || originalniIzvestaj(),
        )
      ) {
        onClose();
      }
      return;
    }
    const uneteAdrese = razdvojiAdrese(unos);
    if (uneteAdrese.some((email) => !EMAIL_REGEX.test(email))) {
      setGreska(t('pdfDialog.neispravnaAdresa'));
      return;
    }
    const adrese = [...primaoci.map((p) => p.email), ...uneteAdrese].filter(
      (email, index, sve) =>
        sve.findIndex((druga) => istaAdresa(druga, email)) === index,
    );
    if (!adrese.length) {
      setGreska(t('pdfDialog.bezPrimalaca'));
      return;
    }
    const podrazumevani = mejlZaTip();
    const mejlZaSlanje = (mejl || jeWorkReport) && {
      naslov: tekstMejla.naslov.trim() || podrazumevani.naslov,
      tekst: tekstMejla.tekst.trim() || podrazumevani.tekst,
    };
    if (
      await posaljiIzvestaj(
        idDok,
        tip,
        jezik,
        adrese,
        izvestaj || originalniIzvestaj(),
        mejlZaSlanje,
      )
    ) {
      onClose();
    }
  };

  const slobodniPredlozi = predlozi.filter(
    (predlog) => !primaoci.some((p) => istaAdresa(p.email, predlog.email)),
  );
  const brojPrimalaca = primaoci.length + razdvojiAdrese(unos).length;
  const jeMejl = akcija === AKCIJA.MEJL;

  return (
    <Dialog
      open={open && imaPristup}
      onClose={isLoading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 7 }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: SVETLO_NARANDZASTA,
            color: colors.ORANGE,
          }}
        >
          <PictureAsPdfIcon />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
            {jeWorkReport ? t('pdfDialog.workReport') : t('pdfReport')}
            {info.ceoBrDok ? ` – ${info.ceoBrDok}` : ''}
          </Typography>
          <Typography noWrap sx={{ fontSize: 13, color: 'text.secondary' }}>
            {info.nazivKupca ||
              (tip === TIP_IZVESTAJA.PUR ? t('commissioning') : t('workOrder'))}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={isLoading}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: '#eee' }}>
        {jeWorkReport && (
          <>
            <NaslovSekcije>{t('pdfDialog.datumIzvestaja')}</NaslovSekcije>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD.MM.YYYY"
                value={dayjs(datum)}
                onChange={(vrednost) =>
                  vrednost?.isValid() &&
                  izmeniDatum(vrednost.format('YYYY-MM-DD'))
                }
                disabled={isLoading}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    helperText: t('pdfDialog.datumIzvestajaOpis'),
                    sx: { mb: 2.5 },
                  },
                }}
              />
            </LocalizationProvider>
          </>
        )}
        <NaslovSekcije>{t('pdfDialog.jezik')}</NaslovSekcije>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={jezik}
          onChange={(e, vrednost) => vrednost && setJezik(vrednost)}
          sx={{
            mb: 2.5,
            '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 14 },
            '& .MuiToggleButton-root.Mui-selected': {
              backgroundColor: SVETLO_NARANDZASTA,
              color: colors.ORANGE,
              fontWeight: 600,
            },
          }}
        >
          <ToggleButton value={JEZIK_IZVESTAJA.SRB}>Srpski</ToggleButton>
          <ToggleButton value={JEZIK_IZVESTAJA.ENG}>English</ToggleButton>
        </ToggleButtonGroup>

        <NaslovSekcije>{t('pdfDialog.akcija')}</NaslovSekcije>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
          <OpcijaAkcije
            izabrana={!jeMejl}
            ikonica={<DownloadIcon />}
            naslov={t('pdfDialog.preuzmi')}
            opis={t('pdfDialog.preuzmiOpis')}
            onClick={() => setAkcija(AKCIJA.PREUZMI)}
          />
          <OpcijaAkcije
            izabrana={jeMejl}
            ikonica={<EmailOutlinedIcon />}
            naslov={t('pdfDialog.mejl')}
            opis={
              ucitavanje ? (
                t('pdfDialog.ucitavanje')
              ) : emailoviKupca.length ? (
                <>
                  {t('pdfDialog.kupcu')}:{' '}
                  <b style={{ color: colors.ORANGE }}>
                    {emailoviKupca.join(', ')}
                  </b>
                </>
              ) : (
                t('pdfDialog.kupacBezEmaila')
              )
            }
            onClick={() => setAkcija(AKCIJA.MEJL)}
          />
        </Box>

        <NaslovSekcije>{t('pdfDialog.sadrzaj')}</NaslovSekcije>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            mb: jeMejl ? 2.5 : 0,
            borderRadius: 2,
            border: izvestaj
              ? `1px solid ${colors.ORANGE}`
              : '1px dashed #d0d0d0',
            backgroundColor: izvestaj ? SVETLO_NARANDZASTA : colors.WHITE,
          }}
        >
          <EditNoteIcon sx={{ color: izvestaj ? colors.ORANGE : '#888' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
              {izvestaj
                ? t('pdfDialog.izmenjeniPodaci')
                : t('pdfDialog.originalniPodaci')}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              {izvestaj
                ? t('pdfDialog.izmenjeniPodaciOpis')
                : t('pdfDialog.originalniPodaciOpis')}
            </Typography>
          </Box>
          {izvestaj && (
            <Tooltip title={t('pdfDialog.vratiOriginal')}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => setIzvestaj(null)}
                  disabled={isLoading}
                >
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title={t('pdfDialog.pregled')}>
            <span>
              <IconButton
                size="small"
                onClick={handlePregled}
                disabled={isLoading}
                sx={{ color: colors.ORANGE }}
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditNoteIcon />}
            onClick={handleIzmeni}
            disabled={isLoading}
            sx={{
              flexShrink: 0,
              textTransform: 'none',
              color: colors.ORANGE,
              borderColor: colors.ORANGE,
              '&:hover': { borderColor: colors.ORANGE },
            }}
          >
            {t('pdfDialog.izmeni')}
          </Button>
        </Box>

        {jeMejl && (
          <>
            <NaslovSekcije>{t('pdfDialog.primaoci')}</NaslovSekcije>
            <Box
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                p: 1.5,
                mb: 1.5,
                minHeight: 52,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {ucitavanje && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: colors.ORANGE }} />
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                    {t('pdfDialog.ucitavanje')}
                  </Typography>
                </Box>
              )}
              {!ucitavanje && !primaoci.length && (
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                  {emailoviKupca.length
                    ? t('pdfDialog.nemaPrimalaca')
                    : `${t('pdfDialog.kupacBezEmaila')} ${t(
                        'pdfDialog.nemaPrimalaca',
                      )}`}
                </Typography>
              )}
              {primaoci.map((primalac) => (
                <Chip
                  key={primalac.email}
                  icon={IKONICE_IZVORA[primalac.izvor]}
                  label={
                    <span>
                      {primalac.email}
                      {primalac.naziv && (
                        <span style={{ color: '#999' }}>
                          {' '}
                          · {primalac.naziv}
                        </span>
                      )}
                    </span>
                  }
                  onDelete={() => ukloniPrimaoca(primalac.email)}
                  sx={{
                    backgroundColor: SVETLO_NARANDZASTA,
                    border: `1px solid ${colors.ORANGE}`,
                    '& .MuiChip-icon': { color: colors.ORANGE },
                    '& .MuiChip-deleteIcon': { color: '#c0a080' },
                    '& .MuiChip-deleteIcon:hover': { color: colors.DELETE },
                  }}
                />
              ))}
            </Box>

            <TextField
              fullWidth
              size="small"
              value={unos}
              placeholder={t('pdfDialog.unosPlaceholder')}
              onChange={(e) => {
                setUnos(e.target.value);
                if (greska) setGreska('');
              }}
              onKeyDown={(e) => {
                if (['Enter', ',', ';'].includes(e.key)) {
                  e.preventDefault();
                  potvrdiUnos();
                }
              }}
              error={Boolean(greska)}
              helperText={greska || t('pdfDialog.pomocUnos')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AlternateEmailIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={potvrdiUnos}
                      disabled={!unos.trim()}
                      sx={{ color: colors.ORANGE }}
                    >
                      <AddIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {slobodniPredlozi.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <NaslovSekcije>{t('pdfDialog.predlozi')}</NaslovSekcije>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {slobodniPredlozi.map((predlog) => (
                    <Chip
                      key={predlog.email}
                      variant="outlined"
                      icon={<AddIcon fontSize="small" />}
                      label={
                        predlog.naziv
                          ? `${predlog.naziv} · ${predlog.email}`
                          : predlog.email
                      }
                      onClick={() => dodajPrimaoca(predlog)}
                      sx={{
                        borderStyle: 'dashed',
                        '& .MuiChip-icon': { color: colors.ORANGE },
                        '&:hover': { borderColor: colors.ORANGE },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Box sx={{ mt: 2.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <NaslovSekcije>{t('pdfDialog.tekstMejla')}</NaslovSekcije>
                {mejl && (
                  <Button
                    size="small"
                    startIcon={<RestartAltIcon fontSize="small" />}
                    onClick={() => setMejl(null)}
                    sx={{ mb: 1, textTransform: 'none', color: colors.ORANGE }}
                  >
                    {t('pdfDialog.vratiPodrazumevani')}
                  </Button>
                )}
              </Box>
              <TextField
                fullWidth
                size="small"
                label={t('pdfDialog.naslovMejla')}
                value={tekstMejla.naslov}
                onChange={(e) => izmeniMejl('naslov', e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={10}
                size="small"
                label={t('pdfDialog.porukaMejla')}
                value={tekstMejla.tekst}
                onChange={(e) => izmeniMejl('tekst', e.target.value)}
              />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <OButton
          text={t('pdfDialog.otkazi')}
          startIcon={icons.DIALOG_CLOSE}
          onClick={onClose}
          disabled={isLoading}
          style={{
            color: colors.ORANGE,
            backgroundColor: colors.WHITE,
            border: `1px solid ${colors.ORANGE}`,
          }}
        />
        <OButton
          text={
            jeMejl
              ? t('pdfDialog.posalji', { broj: brojPrimalaca })
              : t('pdfDialog.preuzmiPdf')
          }
          startIcon={
            jeMejl ? (
              <EmailOutlinedIcon htmlColor={colors.WHITE} />
            ) : (
              <DownloadIcon htmlColor={colors.WHITE} />
            )
          }
          onClick={handlePotvrdi}
          isLoading={isLoading}
          disabled={isLoading || (jeMejl && !brojPrimalaca)}
          style={{
            color: colors.WHITE,
            backgroundColor: colors.ORANGE,
            border: `1px solid ${colors.ORANGE}`,
            opacity: !isLoading && jeMejl && !brojPrimalaca ? 0.5 : 1,
          }}
        />
      </DialogActions>

      <Dialog
        open={Boolean(pregledUrl)}
        onClose={zatvoriPregled}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 3, height: '92vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 7 }}>
          {t('pdfDialog.pregled')}
          <IconButton
            onClick={zatvoriPregled}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {pregledUrl && (
            <iframe
              title={t('pdfDialog.pregled')}
              src={pregledUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          )}
        </DialogContent>
      </Dialog>

      {urednik && jeWorkReport && (
        <IzmenaWorkReportaDialog
          izvestaj={urednik}
          jezik={jezik}
          onClose={() => setUrednik(null)}
          onPrimeni={(model) => {
            setIzvestaj(model);
            setUrednik(null);
          }}
        />
      )}
      {urednik && !jeWorkReport && (
        <IzmenaIzvestajaDialog
          izvestaj={urednik}
          jezik={jezik}
          onClose={() => setUrednik(null)}
          onPrimeni={(model) => {
            setIzvestaj(model);
            setUrednik(null);
          }}
        />
      )}
    </Dialog>
  );
};

export { TIP_IZVESTAJA };
export default IzvestajRadnogNalogaDialog;
